import axios from 'axios';

// Create simple in-memory cache for GET requests
const cache = {
  data: {},
  timeout: {},
  
  // Set default cache time to 2 minutes
  defaultExpiry: 2 * 60 * 1000,
  
  set: function(key, value, expiryTime = this.defaultExpiry) {
    this.data[key] = value;
    
    // Clear any existing timeout
    if (this.timeout[key]) {
      clearTimeout(this.timeout[key]);
    }
    
    // Set timeout to clear cache
    this.timeout[key] = setTimeout(() => {
      delete this.data[key];
      delete this.timeout[key];
    }, expiryTime);
  },
  
  get: function(key) {
    return this.data[key];
  },
  
  has: function(key) {
    return key in this.data;
  },
  
  clear: function(keyPattern = null) {
    if (keyPattern) {
      // Clear specific cache items matching the pattern
      Object.keys(this.data).forEach(key => {
        if (key.includes(keyPattern)) {
          if (this.timeout[key]) {
            clearTimeout(this.timeout[key]);
          }
          delete this.data[key];
          delete this.timeout[key];
        }
      });
    } else {
      // Clear all cache
      Object.keys(this.timeout).forEach(key => {
        clearTimeout(this.timeout[key]);
      });
      this.data = {};
      this.timeout = {};
    }
  }
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://channl.onrender.com/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor to handle token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add cache key to request for later use
    if (config.method === 'get') {
      const cacheKey = `${config.url}${JSON.stringify(config.params || {})}`;
      config.cacheKey = cacheKey;
      
      // Return cached response if available
      if (cache.has(cacheKey) && !config.bypassCache) {
        const cachedResponse = cache.get(cacheKey);
        // This aborts the actual request and returns the cached data
        config.adapter = () => Promise.resolve(cachedResponse);
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to cache GET responses
api.interceptors.response.use(
  (response) => {
    // Cache successful GET requests
    if (response.config.method === 'get' && response.config.cacheKey) {
      // Don't cache if specifically requested not to
      if (!response.config.noCache) {
        cache.set(response.config.cacheKey, response);
      }
    }
    
    // If the response is a mutation (POST, PUT, DELETE), clear relevant cache
    if (['post', 'put', 'patch', 'delete'].includes(response.config.method)) {
      // Extract the base path to clear related cached GET requests
      const url = response.config.url;
      const basePath = url.split('/').slice(0, -1).join('/');
      cache.clear(basePath);
    }
    
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add utility methods to make requests with cache control
const enhancedAPI = {
  ...api,
  
  // Method to bypass cache for a specific request
  getNoCache: function(url, config = {}) {
    return api.get(url, { ...config, bypassCache: true });
  },
  
  // Method to refresh cache with a new request
  refreshCache: function(url, config = {}) {
    return this.getNoCache(url, config).then(response => {
      const cacheKey = `${url}${JSON.stringify(config.params || {})}`;
      cache.set(cacheKey, response);
      return response;
    });
  },
  
  // Method to clear all cache
  clearCache: function(keyPattern = null) {
    cache.clear(keyPattern);
  }
};

export default enhancedAPI; 